import type { ColumnFiltersState, RowData, SortingState } from '@tanstack/react-table';

export type ExportFormat = 'csv' | 'json' | 'xlsx';

// Single source of truth for every ExportFormat value, so a runtime check (e.g.
// validating a persisted preset) can't silently drift from the type as it grows.
export const EXPORT_FORMATS = ['csv', 'json', 'xlsx'] as const satisfies readonly ExportFormat[];

export type ExportRow = Record<string, string | number | null>;

export type ExportableColumn = {
  readonly id: string;
  readonly header: string;
  readonly isVisible: boolean;
  readonly group?: string;
};

export type ExportConfig = {
  readonly columnIds: string[];
  readonly format: ExportFormat;
  readonly filters: ColumnFiltersState;
  readonly sorting: SortingState;
  readonly globalFilter: string | undefined;
};

export type ExportResult =
  | {
      readonly url: string;
      readonly filename?: string;
    }
  | {
      readonly blob: Blob;
      readonly filename: string;
      readonly mimeType: string;
    };

export type ExportFeatureConfig = {
  readonly formats?: ExportFormat[];
  readonly filename?: string | ((format: ExportFormat) => string);
  readonly onExport?: (config: ExportConfig) => Promise<ExportResult>;
};

export type ExportState = {
  readonly status: 'idle' | 'exporting' | 'success' | 'error';
  readonly error: string | null;
};

export type ExportAdditionalColumn<TData, TValue> = {
  readonly id: string;
  readonly header: string;
  readonly isVisible?: boolean;
  readonly value: (value: TValue, row: TData) => string | number | null;
};

export type ExportMeta<TData extends RowData, TValue> = {
  enabled?: boolean;
  header?: string;
  value?: (value: TValue, row: TData) => string | number | null;
  additionalColumns?: readonly ExportAdditionalColumn<TData, TValue>[];
};
